/**
 * Focus management and element navigation utilities
 * 
 * This module provides utilities for:
 * - Focusing elements at specific offsets
 * - Finding next/previous editable elements
 * - Navigating between parameters and paragraphs
 * - Determining if element is last editable
 */

import {getStructureIndex, getParamIndex, queryByIndices, findParagraphAtIndex, getAllEditableElements} from './elementHelpers';
import {createRangeAtOffset, applyRangeToSelection} from './rangeHelpers';
import {getTextContent} from './textHelpers';

export function focusElementAtOffset(element: HTMLElement, offset: number = 0): void {
    if(!element) return;
    
    element.focus();
    const range = createRangeAtOffset(element, offset);
    applyRangeToSelection(range);
}

function findNextParamInSameRow(structureIndex: number, paramIndex: number): HTMLElement | null {
    return queryByIndices(structureIndex, paramIndex + 1);
}

function findPreviousParamInSameRow(structureIndex: number, paramIndex: number): HTMLElement | null {
    if(paramIndex <= 0) return null;
    return queryByIndices(structureIndex, paramIndex - 1);
}

function findEditableRowInDirection(
    structureIndex: number,
    direction: 'next' | 'previous'
): HTMLElement | null {
    const allEditables = getAllEditableElements();
    
    // Get all structure indices and filter based on direction
    const indices = allEditables
        .map(el => getStructureIndex(el))
        .filter(idx => direction === 'next' ? idx > structureIndex : idx < structureIndex)
        .sort((a, b) => direction === 'next' ? a - b : b - a);
    
    if(indices.length === 0) return null;
    
    const targetIndex = indices[0];
    
    if(direction === 'next') {
        // For next navigation, find the first param (index 0) or paragraph
        const nextElement = queryByIndices(targetIndex, 0);
        if(nextElement) return nextElement;
        
        return findParagraphAtIndex(targetIndex);
    } else {
        // For previous navigation, find the LAST editable element in that row
        // First check if there are any params in that row
        const paramsInRow = allEditables.filter(el => {
            const paramIdx = getParamIndex(el);
            return getStructureIndex(el) === targetIndex && paramIdx !== null;
        });
        
        if(paramsInRow.length > 0) {
            // Find the param with the highest index
            const maxParamIndex = Math.max(...paramsInRow.map(el => getParamIndex(el) as number));
            const lastParam = queryByIndices(targetIndex, maxParamIndex);
            if(lastParam) return lastParam;
        }
        
        // If no params found, try to find a regular paragraph row
        return findParagraphAtIndex(targetIndex);
    }
}

function findNextEditableRow(structureIndex: number): HTMLElement | null {
    return findEditableRowInDirection(structureIndex, 'next');
}

function findPreviousEditableRow(structureIndex: number): HTMLElement | null {
    return findEditableRowInDirection(structureIndex, 'previous');
}

export function focusNextEditableElement(currentElement: HTMLElement): boolean {
    if(!currentElement) return false;
    
    const structureIndex = getStructureIndex(currentElement);
    const paramIndex = getParamIndex(currentElement);

    // If this is an inline param, try to find the next param in the same row
    if(paramIndex !== null) {
        const nextParam = findNextParamInSameRow(structureIndex, paramIndex);
        if(nextParam) {
            focusElementAtOffset(nextParam, 0);
            return true;
        }
    }

    // Try to find the next editable row
    const nextElement = findNextEditableRow(structureIndex);
    if(nextElement) {
        focusElementAtOffset(nextElement, 0);
        return true;
    }

    return false;
}

export function focusPreviousEditableElement(currentElement: HTMLElement): boolean {
    if(!currentElement) return false;
    
    const structureIndex = getStructureIndex(currentElement);
    const paramIndex = getParamIndex(currentElement);

    // If this is an inline param, try to find the previous param in the same row
    if(paramIndex !== null && paramIndex > 0) {
        const prevParam = findPreviousParamInSameRow(structureIndex, paramIndex);
        if(prevParam) {
            // Focus at the END of the previous element
            const content = getTextContent(prevParam.innerHTML);
            focusElementAtOffset(prevParam, content.length);
            return true;
        }
    }

    // Try to find the previous editable row
    const prevElement = findPreviousEditableRow(structureIndex);
    if(prevElement) {
        // Focus at the END of the previous element
        const content = getTextContent(prevElement.innerHTML);
        focusElementAtOffset(prevElement, content.length);
        return true;
    }

    return false;
}

function findMaxStructureIndex(elements: Element[]): number {
    if(elements.length === 0) return -1;
    
    return Math.max(...elements.map(el => getStructureIndex(el)));
}

function findMaxParamIndex(elements: Element[], structureIndex: number): number | null {
    const paramsAtIndex = elements.filter(el => {
        const paramIdx = getParamIndex(el);
        return getStructureIndex(el) === structureIndex && paramIdx !== null;
    });
    
    if(paramsAtIndex.length === 0) return null;
    
    const paramIndices = paramsAtIndex
        .map(el => getParamIndex(el))
        .filter((idx): idx is number => idx !== null);
    
    return Math.max(...paramIndices);
}

export function isLastEditableElement(
    element: HTMLElement,
    allEditables?: Element[]
): boolean {
    if(!element) return false;
    
    const currentStructureIndex = getStructureIndex(element);
    const currentParamIndex = getParamIndex(element);
    
    const editables = allEditables ?? getAllEditableElements();
    if(editables.length === 0) return false;
    
    const maxStructureIndex = findMaxStructureIndex(editables);
    
    // Current element is not at max structure index
    if(currentStructureIndex !== maxStructureIndex) {
        return false;
    }
    
    // Find max param index at this structure index
    const maxParamIndex = findMaxParamIndex(editables, maxStructureIndex);
    
    if(maxParamIndex !== null) {
        // Has params - check if current is the max param
        return currentParamIndex === maxParamIndex;
    }
    
    // No params at this index - current element is last
    return true;
}

function findMinStructureIndex(elements: Element[]): number {
    if(elements.length === 0) return -1;
    
    return Math.min(...elements.map(el => getStructureIndex(el)));
}

function findMinParamIndex(elements: Element[], structureIndex: number): number | null {
    const paramsAtIndex = elements.filter(el => {
        const paramIdx = getParamIndex(el);
        return getStructureIndex(el) === structureIndex && paramIdx !== null;
    });
    
    if(paramsAtIndex.length === 0) return null;
    
    const paramIndices = paramsAtIndex
        .map(el => getParamIndex(el))
        .filter((idx): idx is number => idx !== null);
    
    return Math.min(...paramIndices);
}

export function isFirstEditableElement(
    element: HTMLElement,
    allEditables?: Element[]
): boolean {
    if(!element) return false;
    
    const currentStructureIndex = getStructureIndex(element);
    const currentParamIndex = getParamIndex(element);
    
    const editables = allEditables ?? getAllEditableElements();
    if(editables.length === 0) return false;
    
    const minStructureIndex = findMinStructureIndex(editables);
    if(minStructureIndex === -1) return false;
    
    // Current element is not at min structure index
    if(currentStructureIndex !== minStructureIndex) {
        return false;
    }
    
    // Find min param index at this structure index
    const minParamIndex = findMinParamIndex(editables, minStructureIndex);
    
    if(minParamIndex !== null) {
        // Has params - check if current is the min param
        return currentParamIndex === minParamIndex;
    }
    
    // No params at this index - current element is first
    return true;
}
